/**
 * MifeLife v2 — Public shared company report (/r/:token).
 *
 * Read-only, no-auth view of a Company Health Scan, built for sharing with
 * decision-makers inside a company. Includes a professional PDF download and a
 * viral CTA driving viewers to run their own free scan.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Sparkles, Download, Loader2, TrendingUp, TrendingDown, ArrowRight, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import V2Nav from '../../components/v2/V2Nav';
import V2Footer from '../../components/v2/V2Footer';
import { apiRequest, downloadFile } from '../../lib/utils';

const DIM_TONE = {
  finans:    { bg: 'bg-emerald-500/10', border: 'border-emerald-400/30', text: 'text-emerald-300', bar: 'bg-emerald-400' },
  pazarlama: { bg: 'bg-amber-500/10',   border: 'border-amber-400/30',   text: 'text-amber-300',   bar: 'bg-amber-400' },
  satis:     { bg: 'bg-blue-500/10',    border: 'border-blue-400/30',    text: 'text-blue-300',    bar: 'bg-blue-400' },
  operasyon: { bg: 'bg-violet-500/10',  border: 'border-violet-400/30',  text: 'text-violet-300',  bar: 'bg-violet-400' },
};
const DIM_LABEL = { finans: 'Finans', pazarlama: 'Pazarlama', satis: 'Satış', operasyon: 'Operasyon' };
const DIM_TO_SPECIALTY = { finans: 'Finans Danışmanı', pazarlama: 'Pazarlama Danışmanı', satis: 'Satış Koçu', operasyon: 'Operasyon Danışmanı' };

export default function V2SharedReportPage() {
  const { token } = useParams();
  const [lang, setLang] = useState('tr');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    document.title = 'Şirket Sağlık Raporu · MifeLife';
    apiRequest(`/v2/company-analysis/shared/${token}`)
      .then(setReport)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const name = (report?.company_name || 'mifelife-sirket-analizi').replace(/\s+/g, '-');
      await downloadFile(`/v2/company-analysis/shared/${token}/pdf?lang=${lang}`, `${name}-rapor.pdf`);
    } catch (e) {
      toast.error('PDF indirilemedi.');
    } finally {
      setPdfLoading(false);
    }
  };

  const dims = ['finans', 'pazarlama', 'satis', 'operasyon'];

  return (
    <div className="min-h-screen bg-[#0F0F1A] text-white antialiased" data-testid="v2-shared-report-page">
      <V2Nav lang={lang} onLangChange={setLang} />

      <section className="pt-28 sm:pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          {loading && (
            <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 text-violet-300 animate-spin" /></div>
          )}

          {!loading && error && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center" data-testid="v2-shared-error">
              <ShieldCheck className="w-10 h-10 text-slate-600 mx-auto mb-4" />
              <h1 className="text-xl font-semibold mb-2">Bu rapor mevcut değil</h1>
              <p className="text-sm text-slate-400 mb-6">Bağlantı geçersiz olabilir veya rapor sahibi paylaşımı kapatmış olabilir.</p>
              <Link to="/sirket" className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-semibold hover:from-violet-400 hover:to-violet-500 transition" data-testid="v2-shared-cta-error">
                Kendi şirketini ücretsiz analiz et <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}

          {!loading && report && (
            <div data-testid="v2-shared-report">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-violet-300 mb-3">
                <ShieldCheck className="w-3.5 h-3.5" /> Paylaşılan rapor · MifeLife
              </div>
              <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
                <div>
                  <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-2">Şirket Sağlık Taraması</h1>
                  <p className="text-sm text-slate-400">
                    {[report.company_name, report.industry, report.employee_count && `${report.employee_count} kişi`].filter(Boolean).join('  ·  ') || 'Şirket raporu'}
                  </p>
                </div>
                <button onClick={downloadPdf} disabled={pdfLoading} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg border border-white/10 bg-white/[0.02] text-sm text-white hover:bg-white/[0.05] disabled:opacity-50 transition" data-testid="v2-shared-download-pdf">
                  {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} PDF indir
                </button>
              </div>

              {/* score cards */}
              <div className="grid sm:grid-cols-2 gap-3 mb-8">
                {dims.map((d) => {
                  const t = DIM_TONE[d];
                  const score = report.scores?.[d] ?? 0;
                  return (
                    <div key={d} className={`rounded-2xl border ${t.border} ${t.bg} p-5`} data-testid={`v2-shared-score-${d}`}>
                      <div className="flex items-baseline justify-between mb-2">
                        <span className={`text-sm font-semibold ${t.text}`}>{DIM_LABEL[d]}</span>
                        <span className="text-2xl font-semibold tabular-nums text-white">%{score}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                        <div className={`h-full ${t.bar}`} style={{ width: `${score}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* strongest / weakest */}
              <div className="grid sm:grid-cols-2 gap-3 mb-8">
                <div className="rounded-xl border border-emerald-400/20 bg-emerald-500/[0.04] p-4 flex items-center gap-3">
                  <TrendingUp className="w-4 h-4 text-emerald-300" />
                  <div><div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">En güçlü</div><div className="text-base font-semibold">{DIM_LABEL[report.strongest_dim]}</div></div>
                </div>
                <div className="rounded-xl border border-rose-400/20 bg-rose-500/[0.04] p-4 flex items-center gap-3">
                  <TrendingDown className="w-4 h-4 text-rose-300" />
                  <div><div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Öncelikli gelişim</div><div className="text-base font-semibold">{DIM_LABEL[report.weakest_dim]}</div></div>
                </div>
              </div>

              {/* roadmap */}
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 sm:p-8 mb-8">
                <h2 className="text-lg font-semibold mb-5 flex items-center gap-2"><Sparkles className="w-4 h-4 text-violet-300" /> Yol haritası — sıradaki 3 adım</h2>
                <ol className="space-y-3" data-testid="v2-shared-roadmap">
                  {(report.roadmap_steps || []).map((s, i) => (
                    <li key={i} className="flex gap-3 text-sm text-slate-300 leading-relaxed">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-500/15 border border-violet-400/30 text-violet-200 flex items-center justify-center text-xs font-semibold">{i + 1}</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ol>
              </div>

              {/* viral CTA */}
              <div className="rounded-2xl border border-violet-400/25 bg-gradient-to-br from-violet-500/[0.08] to-cyan-500/[0.04] p-6 sm:p-8 text-center" data-testid="v2-shared-viral-cta">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/15 border border-violet-400/30 text-[11px] uppercase tracking-[0.18em] text-violet-200 mb-4">
                  <Sparkles className="w-3 h-3" /> MifeLife
                </div>
                <h2 className="text-xl sm:text-2xl font-semibold tracking-tight mb-2">Kendi şirketinin sağlık taramasını yap</h2>
                <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto">8 soru, 3 dakika. AI şirket profilini çıkarır, en zayıf alanında uzman mentör önerir. %100 ücretsiz.</p>
                <Link to="/sirket" className="inline-flex items-center gap-2 h-12 px-6 rounded-lg bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-semibold hover:from-violet-400 hover:to-violet-500 transition" data-testid="v2-shared-cta">
                  Ücretsiz taramayı başlat <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      <V2Footer lang={lang} />
    </div>
  );
}
